import type {
  AiModelDraft,
  AiModelEntry,
  AiModelHealth,
  AiProviderId,
  AiProviderInfo,
  AiSettingsInput,
  AiSettingsView,
} from "./callables";

/**
 * O rascunho da tela de IA e a conversão para o que `adminSetAiSettings`
 * recebe. Puro, sem React.
 *
 * A regra da chave é a do backend (`utils/aiSettings.ts` do zepadoca-functions):
 * a chave digitada agora substitui a salva; vazia, vale a salva no mesmo item;
 * e um item pode herdar a chave do item anterior do mesmo provedor.
 */

export interface AiEntryDraft {
  /** Chave do React, estável enquanto a tela está aberta. */
  key: string;
  /** O id gravado no servidor; ausente no item novo. */
  id?: string;
  provider: AiProviderId;
  model: string;
  /** A pessoa escolheu digitar o nome do modelo. */
  customModel: boolean;
  /** A chave digitada agora. Vazia: mantém a salva (ou herda). */
  apiKey: string;
  /** Falso: herda a chave do item anterior do mesmo provedor. */
  useOwnKey: boolean;
  /** O provedor e o fim da chave salva, para a tela dizer o que vale. */
  savedProvider: AiProviderId | null;
  savedKeyHint: string | null;
  health: AiModelHealth | null;
}

export interface AiFeatureDraft {
  enabled: boolean;
  instructions: string;
  dailyLimit: string;
}

export interface AiSettingsDraft {
  entries: AiEntryDraft[];
  generalInstructions: string;
  features: Record<string, AiFeatureDraft>;
}

let keySeed = 0;
function nextKey(): string {
  keySeed += 1;
  return `entrada-${keySeed}`;
}

export function newEntryDraft(provider: AiProviderInfo): AiEntryDraft {
  return {
    key: nextKey(),
    provider: provider.id,
    model: provider.defaultModel,
    customModel: false,
    apiKey: "",
    useOwnKey: true,
    savedProvider: null,
    savedKeyHint: null,
    health: null,
  };
}

function entryFromView(entry: AiModelEntry): AiEntryDraft {
  return {
    key: nextKey(),
    id: entry.id,
    provider: entry.provider,
    model: entry.model,
    // O modelo salvo aparece na lista mesmo quando o provedor não o lista:
    // o campo de texto só abre quando a pessoa pede.
    customModel: false,
    apiKey: "",
    useOwnKey: entry.hasOwnKey || entry.inheritsFromIndex === null,
    savedProvider: entry.hasOwnKey ? entry.provider : null,
    savedKeyHint: entry.keyHint,
    health: entry.health,
  };
}

/** O rascunho a partir do que o servidor devolveu. Sem fila, começa com um item. */
export function draftFromView(view: AiSettingsView): AiSettingsDraft {
  const entries = view.models.map(entryFromView);
  if (entries.length === 0 && view.providers[0]) entries.push(newEntryDraft(view.providers[0]));
  const features: Record<string, AiFeatureDraft> = {};
  for (const feature of view.features) {
    features[feature.id] = {
      enabled: feature.enabled,
      instructions: feature.instructions,
      dailyLimit: String(feature.dailyLimit),
    };
  }
  return { entries, generalInstructions: view.generalInstructions, features };
}

/** O índice do item anterior do mesmo provedor, de quem este pode herdar a chave. */
export function inheritSourceIndex(entries: AiEntryDraft[], index: number): number | null {
  for (let i = index - 1; i >= 0; i--) {
    if (entries[i].provider === entries[index].provider) return i;
  }
  return null;
}

/** Verdadeiro quando o item vai herdar a chave em vez de usar a própria. */
export function inheritsKey(entries: AiEntryDraft[], index: number): boolean {
  return !entries[index].useOwnKey && inheritSourceIndex(entries, index) !== null;
}

/** A chave salva vale para este item: mesmo provedor, e nada digitado por cima. */
export function hasSavedKey(entry: AiEntryDraft): boolean {
  return Boolean(entry.savedKeyHint) && entry.savedProvider === entry.provider;
}

/** O fim da chave salva ("MiKw"), sem as reticências do servidor. */
export function savedKeyEnd(entry: Pick<AiEntryDraft, "savedKeyHint">): string {
  return (entry.savedKeyHint ?? "").replace(/^\.+/, "");
}

/**
 * O que o campo da chave mostra quando há chave salva e nada digitado: pontos
 * e o fim dela. Não é a chave: ela fica no servidor e não volta para a tela.
 * O campo cheio deixa claro que a chave está lá e continua ao trocar o modelo.
 */
export function savedKeyMask(entry: Pick<AiEntryDraft, "savedKeyHint">): string {
  return `••••••••••••${savedKeyEnd(entry)}`;
}

export function toModelDrafts(entries: AiEntryDraft[]): AiModelDraft[] {
  return entries.map((entry, index) => {
    const inherit = inheritsKey(entries, index);
    const typed = entry.apiKey.trim();
    return {
      ...(entry.id ? { id: entry.id } : {}),
      provider: entry.provider,
      model: entry.model.trim(),
      ...(typed && !inherit ? { apiKey: typed } : {}),
      useOwnKey: !inherit,
    };
  });
}

export function toSettingsInput(draft: AiSettingsDraft): AiSettingsInput {
  const features: AiSettingsInput["features"] = {};
  for (const [id, feature] of Object.entries(draft.features)) {
    features[id] = {
      enabled: feature.enabled,
      instructions: feature.instructions,
      dailyLimit: Number(feature.dailyLimit),
    };
  }
  return {
    models: toModelDrafts(draft.entries),
    generalInstructions: draft.generalInstructions,
    features,
  };
}

/**
 * O problema do rascunho antes de enviar, ou `null`. O servidor confere de
 * novo; aqui é só para a mensagem aparecer sem a ida e volta.
 */
export function draftProblem(draft: AiSettingsDraft, view: AiSettingsView): string | null {
  for (const [index, entry] of draft.entries.entries()) {
    const position = index + 1;
    if (!entry.model.trim()) return `Escolha o modelo ${position}.`;
    if (inheritsKey(draft.entries, index)) continue;
    if (!entry.apiKey.trim() && !hasSavedKey(entry)) return `Informe a chave de API do modelo ${position}.`;
  }
  for (const feature of view.features) {
    const value = Number(draft.features[feature.id]?.dailyLimit);
    if (!Number.isInteger(value) || value < 1 || value > feature.maxDailyLimit) {
      return `O limite diário de "${feature.label}" precisa ser um número inteiro de 1 a ${feature.maxDailyLimit}.`;
    }
  }
  return null;
}

/**
 * De onde vem a chave usada para listar os modelos de um item: a digitada
 * (pelo tamanho e os 6 últimos caracteres, que entram na chave de consulta
 * sem a chave inteira), a salva no item, ou a herdada. `null`: não há chave.
 */
export function credentialTag(entries: AiEntryDraft[], index: number): string | null {
  const entry = entries[index];
  if (inheritsKey(entries, index)) {
    const source = inheritSourceIndex(entries, index) as number;
    return credentialTag(entries, source);
  }
  const typed = entry.apiKey.trim();
  if (typed) return `digitada:${typed.length}:${typed.slice(-6)}`;
  if (hasSavedKey(entry) && entry.id) return `salva:${entry.id}`;
  return null;
}

/** "Em pausa até 14:32" para a quarentena que ainda vale. */
export function healthUntilLabel(health: AiModelHealth, now: number): string | null {
  const until = Date.parse(health.until);
  if (!Number.isFinite(until) || until <= now) return null;
  const time = new Date(until).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `Em pausa até ${time}`;
}
